import { Injectable } from '@angular/core';

import { OptionMarketsRequest } from '@cds/betting-offer/bet-builder';
import { MarketGroupConfig } from '@cds/betting-offer/grouping/bet-builder';
import { BetBuilderConfig } from '@frontend/sports/common/client-config-data-access';
import { Page } from '@frontend/vanilla/core';
import { sortBy } from 'lodash-es';
import { Observable, firstValueFrom, map } from 'rxjs';

import { BetBuilderViewModel } from './bet-builder-view-model';
import { MarketBuilderFactory } from './market-builder-factory';
import { MarketBuilderProvider } from './market-builder-provider.service';

@Injectable({
    providedIn: 'root',
})
export class MarketBuilderService {
    constructor(
        private betBuilderConfig: BetBuilderConfig,
        private factory: MarketBuilderFactory,
        private page: Page,
        private provider: MarketBuilderProvider,
    ) {}

    async getMarketGroupConfig(sportId: number, version?: string): Promise<MarketGroupConfig> {
        const grouping = await firstValueFrom(this.provider.getMarketGroupConfig(sportId, version));

        if (!grouping) {
            throw new Error('Unable to load market grouping');
        }

        // entrypoint group should be first
        grouping.marketGroups = sortBy(grouping.marketGroups, (g) => !g.isEntryPoint);

        return grouping;
    }

    getMarketItems(
        sportId: number,
        sportcastId: number,
        leagueId: number,
        marketGroupId: string,
        fixtureId?: string,
    ): Observable<Promise<BetBuilderViewModel>> {
        const request: OptionMarketsRequest = {
            sportcastCulture: this.getSportcastCulture(),
            marketGroupId,
            sportcastRegion: this.getSportcastRegion(sportId, leagueId),
            sportId,
            sportcastFixtureId: sportcastId,
            fixtureId,
        };

        return this.provider.getMarketList(request).pipe(
            map(async (view) => {
                const config = await this.getMarketGroupConfig(request.sportId, view?.version);
                const marketGroup = config.marketGroups.find((mg) => mg.id === marketGroupId);
                if (!marketGroup) {
                    throw new Error('Unable to find matching market group');
                }
                const optionGroups = this.factory.getOptionGroups(
                    config,
                    view?.betBuilderOptionMarkets || [],
                    marketGroup,
                    sportcastId,
                    request.sportId,
                );
                const betbuilderView: BetBuilderViewModel = new BetBuilderViewModel();
                betbuilderView.fixture = view?.fixture;
                betbuilderView.eventOptionGroups = optionGroups;
                betbuilderView.availableMarketGroups = view?.availableMarketGroups;

                return betbuilderView;
            }),
        );
    }

    private getSportcastCulture(): string {
        return this.page.culture.startsWith('en') ? (this.betBuilderConfig.betGeniusWidgetLanguage ?? '') : this.page.culture;
    }

    private getSportcastRegion(sportId: number, leagueId: number): string {
        const none = '';
        const params = this.betBuilderConfig.betGeniusIframeUrlParameters;
        if (!params) {
            return none;
        }
        const regionConfig = params['region'];
        if (!regionConfig) {
            return none;
        }
        const takeRegionFromConfig =
            (!regionConfig.sportIds && !regionConfig.leagueIds) ||
            regionConfig.sportIds?.includes(sportId) ||
            regionConfig.leagueIds?.includes(leagueId);

        return takeRegionFromConfig ? regionConfig.value : none;
    }
}
